import Config from 'config/settings';
import { Events } from 'core/events/Events';
import { AppEvents } from 'app/events/AppEvents';
import { SectionType, AboutSectionType } from 'app/data/Enums';
import { device } from 'utils/device';
import { Simple } from 'utils/analytics';
import { NavMenuAboutFlowDeps } from './navmenu.types';

/**
 * The about/info section switching flow: owns the info-section-active state,
 * the background-scale / about-content-x geometry (CSS custom properties +
 * the resize re-apply) and the info/home/department link routing decisions.
 * Cross-controller calls resolve in NavMenuController via the injected deps;
 * Events.on registrations stay in the controller (handlers delegate here).
 */
export class NavMenuAboutFlow {
	private _deps: NavMenuAboutFlowDeps;

	private _infoSectionActive: boolean = false;
	private _innerWidth: number;

	constructor(deps: NavMenuAboutFlowDeps) {
		this._deps = deps;
	}

	//Info section state
	//-------------------------------------------------------------------------------------

	public get infoSectionActive(): boolean {
		return this._infoSectionActive;
	}

	public set infoSectionActive(value: boolean) {
		this._infoSectionActive = value;
	}

	/** Record the inner width on menu open (moved intact from _setMenuState) */
	public recordInnerWidth(): void {
		this._innerWidth = device.innerWidth;
	}

	//Link routing
	//-------------------------------------------------------------------------------------

	//**  Info menu button click - move to the info section and deep link to correct subsection */
	public onMenuInfoLinkClick(e: MouseEvent): boolean {
		let $link: Element = e.target as HTMLElement;
		let infoSection: AboutSectionType = $link.getAttribute('data-infosection') as AboutSectionType;
		let sectionIndex: number = parseInt($link.getAttribute('data-index'), 10);

		if (infoSection === AboutSectionType.HOME || infoSection === AboutSectionType.WORK) {
			return this.processHomeClick(infoSection);
		}

		this._infoSectionActive = true;

		//Animate off the container if not already on info:
		if (this._deps.getState().section !== SectionType.ABOUT) {
			//Store scroll state for when coming back to
			if (this._deps.getOpeningState().section === SectionType.PROJECT) {
				//KNOWN PRE-EXISTING BUG (tracked separately — preserved, NOT fixed):
				//resolves to this.get('project').aboutSectionActive(), which does not
				//exist on ProjectController. Moved verbatim from NavMenuController.
				this._deps.projectAboutSectionActive();
			}

			Simple.event('nav_menu_info_item_click', { section: infoSection });
			this.transitionToInfo();
		} else {
			Simple.event('sub_nav_info_item_click', { section: infoSection });
		}

		//Goggle analtics:

		//If the INFO section is already active then its a transition within a section.
		if (this._deps.getState().section === SectionType.ABOUT) {
			this._deps.aboutNavMenuPressItemPress(sectionIndex);
		}

		//Do Change section event:
		Events.dispatch(AppEvents.CHANGE_SECTION, {
			href: $link.getAttribute('href'),
			section: SectionType.ABOUT,
			subsection: infoSection,
			fromMenu: true,
			title: this._deps.getAboutSubsectionTitle(infoSection)
		});

		//Make sure events etc are cancelled:
		return true;
	}

	//Process home click, return if should block default link
	public processHomeClick(infoSection: AboutSectionType): boolean {
		let isQuickClose =
			(this._deps.getOpeningState().section == SectionType.WORK && infoSection == AboutSectionType.WORK) ||
			(this._deps.getOpeningState().section == SectionType.HOME && (infoSection as String) == SectionType.HOME);

		if (isQuickClose) {
			this._deps.clickMenuButton();

			try {
				Events.dispatch(AppEvents.DEBUG_LOGO_PRESS, { isMenuHomeClose: (infoSection as String) === SectionType.HOME });
			} catch (e) {
				console.log(e);
			}

			return true;
		}

		//Work LITE link:

		//Is home  link fom work:

		window.location.href = '/';
		return false;
	}


	//NOTE: dead code moved intact from NavMenuController — only call site is the
	//unreachable tail (after `return false`) of the .navmenu__back click handler.
	public onBackToMainView(e: MouseEvent): void {
		gsap.TweenMax.to(this._deps.getContentEl(), 0.9, {
			x: '0%',
			ease: Config.standardEase,
			onComplete: () => {
				this._deps.onBlockAboutScroll();
			}
		});

		this._infoSectionActive = false;
		this._deps.aboutBackToMenuMainView();
		this._deps.setMouseDragCursorActive(false);
		this._deps.setScrollbarActive(false);

		if (!this._deps.getFlagNoHistoryStateOff()) {
			Events.dispatch(AppEvents.CHANGE_SECTION, {
				href: '#menu',
				title: 'Menu',
				section: SectionType.ABOUT,
				subsection: 'menu',
				fromMenu: true
			});
		} else {
			this._deps.setMenuSelectedState();
		}

		this._deps.setFlagNoHistoryStateOff(false);
	}

	//Geometry (CSS custom properties)
	//-------------------------------------------------------------------------------------

	public transitionToInfo(): void {
		(this._deps.getContainer() as HTMLElement).style.setProperty('--navmenu-bg-x-scale', `${this._getBackgroundScale()}`);
		(this._deps.getContainer() as HTMLElement).style.setProperty(
			'--navmenu-about-content-x',
			`${this._infoContentPosition()}`
		);

		if (device.isPortraitDevice) {
		} else {
		}

		this._deps.onAllowAboutScroll();
	}

	public handleResize(): void {
		if (this._deps.isDeeplink() || !this._deps.getContainer()) {
			return;
		}

		if (this._innerWidth === device.innerWidth) {
			return;
		}

		this._innerWidth = device.innerWidth;
		(this._deps.getContainer() as HTMLElement).style.setProperty('--navmenu-bg-x-scale', `${this._getBackgroundScale()}`);
		(this._deps.getContainer() as HTMLElement).style.setProperty(
			'--navmenu-about-content-x',
			`${this._infoContentPosition()}`
		);
	}

	private _infoContentPosition(): string {
		if (!this._infoSectionActive) {
			return '0px';
		}

		if (device.isPortraitDevice) {
			return `${-1 * device.innerWidth}px`;
		}

		let navMenuWidth = 333;
		return `${-1 * (device.innerWidth - navMenuWidth)}px`;

		return;
	}

	private _getBackgroundScale(): number {
		if (this._infoSectionActive) {
			if (device.isPortraitDevice) {
				let widthRatio = 0.4;
				if (device.innerWidth >= Config.mobileBreakpoint)
					return (device.innerWidth - 95) / (device.innerWidth * widthRatio);
				return (device.innerWidth - 55) / (device.innerWidth * widthRatio);
			}

			let navMenuWidth = 333;
			return device.innerWidth / navMenuWidth;
		}

		return 1;
	}
}
